import React, {useCallback, useEffect, useMemo, useState} from "react";
import {IconButton, TextField, Typography} from "@mui/material";
import {useMediaQuery} from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import {RotateLeft} from "@mui/icons-material";
import {DatePicker} from "@mui/x-date-pickers";
import {AuditLogGrid} from "../components/grids/AuditLogGrid";
import {apiGet, apiPost, currentDateObject} from "../utils";
import {RollbackDialog} from "../components/dialogs/RollbackDialog";

export const AuditContainer = () => {
    const today = useMemo(() => currentDateObject(), []);
    const tomorrow = useMemo(() => currentDateObject().add(1, 'days'), []);
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(tomorrow);
    const [fromEventId, setFromEventId] = useState('');
    const [toEventId, setToEventId] = useState('');
    const [auditRecords, setAuditRecords] = useState({});
    const [selectedAuditRecords, setSelectedAuditRecords] = useState([]);
    const [rolledBackRecords, setRolledBackRecords] = useState([]);
    const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const isMobile = useMediaQuery('(max-width: 1510px)');

    const fetchAuditRecordsFromDates = useCallback((from, to) => {
        const fromDateStr = from ? from.format('YYYY-MM-DD') : null;
        const toDateStr = to ? to.format('YYYY-MM-DD') : null;
        apiGet(`admin/audit/dates?from=${fromDateStr}&to=${toDateStr}`, (data) => {
            setAuditRecords(data.data);
            setSelectedAuditRecords([]);
        });
    }, []);

    const fetchAuditRecordsByEventIds = () => {
        apiGet(`admin/audit/ids?eventIds=${selectedAuditRecords.join(',')}`, (data) => {
            setAuditRecords(data.data);
            setSelectedAuditRecords([]);
        });
    };

    const fetchAuditRecordsByEventIdRange = () => {
        apiGet(`admin/audit/ids/range?from=${fromEventId}&to=${toEventId}`, (data) => {
            setAuditRecords(data.data);
            setSelectedAuditRecords([]);
        });
    };

    const refresh = useCallback(() => {
        setFromDate(today);
        setToDate(tomorrow);
        setFromEventId('');
        setToEventId('');
        setSelectedAuditRecords([]);
        fetchAuditRecordsFromDates(today, tomorrow);
    }, [today, tomorrow, fetchAuditRecordsFromDates]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const onFromDateChange = (newValue) => {
        setFromDate(newValue);
    };

    const onToDateChange = (newValue) => {
        setToDate(newValue);
    };

    const onFromEventIdChange = (event) => {
        setFromEventId(event.target.value);
    };

    const onToEventIdChange = (event) => {
        setToEventId(event.target.value);
    };

    const fromEventIdSetter = (eventId) => {
        setFromEventId(eventId);
    };

    const toEventIdSetter = (eventId) => {
        setToEventId(eventId);
    };

    const handleSelectCheckboxClick = (row, checked) => {
        if (checked) {
            setSelectedAuditRecords([...selectedAuditRecords, row.event_id]);
        } else {
            setSelectedAuditRecords(selectedAuditRecords.filter(id => id !== row.event_id));
        }
    };

    const handleOpenRollbackDialog = () => {
        setRollbackDialogOpen(true);
    };

    const handleCloseRollbackDialog = () => {
        setRollbackDialogOpen(false);
    };

    const submitAuditRollback = () => {
        console.log(`Rolling back audit records: ${JSON.stringify(selectedAuditRecords)}`);
        setRollbackDialogOpen(false);
        setLoading(true);
        const auditPostBody = {
            eventIds: selectedAuditRecords
        }
        apiPost(`admin/audit/rollback`, auditPostBody, (data) => {
            console.log(`Rollback results: ${JSON.stringify(data, null, 2)}`);
            setRolledBackRecords(data.data);
            setLoading(false);
        });
    };

    const justify = isMobile ? 'flex-start' : 'center';
    const gridCellStyle = {backgroundColor: "#F2F2F2", border: "10px white solid"}; //TODO: use class from index.css...
    return (
        <Grid container justifyContent={justify}>
            <Grid container justifyContent={'center'} spacing={1}
                  style={{maxWidth: 1440, minWidth: 1440, marginTop: 30, marginBottom: 60, fontSize: 12}}>
                <Grid size={12}>
                    <div style={{float: "right"}}>
                        <IconButton color="inherit" style={{height: 25}} onClick={refresh}>
                            <RotateLeft/>
                        </IconButton>
                    </div>
                    <br/>
                    {loading && <LinearProgress color="secondary"/>}
                </Grid>
                <Grid size={12} style={gridCellStyle}>
                    <Typography variant="h6" color="inherit" display="inline" style={{marginLeft: 5}}>
                        Audit records:
                    </Typography>
                    <DatePicker label="From" value={fromDate} sx={{margin: 1, marginLeft: 3, maxWidth: 150}}
                                onChange={onFromDateChange}/>
                    <DatePicker label="To" value={toDate} sx={{margin: 1, maxWidth: 150}} onChange={onToDateChange}/>
                    <Button variant="contained" type="submit" color="primary" style={{margin: 15, marginLeft: 3}}
                            onClick={() => fetchAuditRecordsFromDates(fromDate, toDate)}>Fetch</Button>
                    <TextField label="From event ID" value={fromEventId} onChange={e => onFromEventIdChange(e)}
                               sx={{margin: 1, marginLeft: 3, maxWidth: 150}}/>
                    <TextField label="To event ID" value={toEventId} onChange={e => onToEventIdChange(e)}
                               sx={{margin: 1, maxWidth: 150}}/>
                    <Button variant="contained" type="submit" color="primary" style={{margin: 15, marginLeft: 3}}
                            onClick={fetchAuditRecordsByEventIdRange}>Fetch</Button>
                    <Button variant="contained" type="submit" color="primary" style={{margin: 15}}
                            onClick={fetchAuditRecordsByEventIds} disabled={selectedAuditRecords.length === 0}>Fetch
                        Selected</Button>
                    <Button variant="contained" type="submit" color="primary" style={{margin: 15}}
                            onClick={handleOpenRollbackDialog} disabled={selectedAuditRecords.length === 0}>Rollback</Button>
                </Grid>
                <Grid size={12}>
                    <AuditLogGrid rows={auditRecords} selectedAuditRecords={selectedAuditRecords}
                                  rolledBackRecords={rolledBackRecords}
                                  onSelectCheckboxClick={handleSelectCheckboxClick}
                                  fromIdSetter={fromEventIdSetter} toIdSetter={toEventIdSetter}/>
                    <br/>
                </Grid>
            </Grid>
            <RollbackDialog open={rollbackDialogOpen} handleClose={handleCloseRollbackDialog}
                            eventIds={selectedAuditRecords} handleRollback={submitAuditRollback}/>
        </Grid>
    );
}